import { useEffect, type ReactElement } from "react";

import type { ArtworkController } from "./artwork-controller";
import type { ConfiguratorController } from "./configurator-controller";
import type { DraftController } from "./draft-controller";
import type { OrderLineController } from "./order-line-controller";
import type { PricingController } from "./pricing-controller";
import type { ProductPageController } from "./product-page-controller";
import { PvcProductPage, type PvcProductPageProps } from "./pvc-product-page";

export const PVC_SIGN_ROUTE_PATH = "/pvc-sign" as const;

export type PvcSignRuntime = {
  page: ProductPageController;
  configurator: ConfiguratorController;
  pricing: PricingController;
  artwork: ArtworkController;
  orderLine: OrderLineController;
  draft: DraftController;
};

export type PvcSignRouteProps = PvcProductPageProps & {
  /**
   * The route is the default composition/lifecycle boundary. Set false only
   * when a higher-level app container owns the same controller instances.
   */
  disposeOnUnmount?: boolean;
};

/**
 * Flush only an actually dirty draft. An unconditional flush would resurrect
 * a draft that submit() deliberately cleared after a successful add/update.
 */
export function disposePvcSignRuntime(runtime: PvcSignRuntime): void {
  if (runtime.draft.getState().status === "dirty") {
    runtime.page.flushDraft();
  }

  // Stop orchestration before aborting/disposing the child controllers.
  runtime.page.dispose();
  runtime.artwork.dispose();
  runtime.orderLine.dispose();
  runtime.pricing.dispose();
  runtime.configurator.dispose();
  runtime.draft.dispose();
}

export function PvcSignRoute(props: PvcSignRouteProps): ReactElement {
  const {
    disposeOnUnmount = true,
    page,
    configurator,
    pricing,
    artwork,
    orderLine,
    draft,
    ...pageProps
  } = props;

  useEffect(() => {
    if (!disposeOnUnmount) return;

    return () => {
      disposePvcSignRuntime({ page, configurator, pricing, artwork, orderLine, draft });
    };
  }, [disposeOnUnmount, page, configurator, pricing, artwork, orderLine, draft]);

  return (
    <PvcProductPage
      {...pageProps}
      page={page}
      configurator={configurator}
      pricing={pricing}
      artwork={artwork}
      orderLine={orderLine}
      draft={draft}
    />
  );
}

/**
 * Router-neutral route-object shape. It is intentionally compatible with the
 * common { path, element } contract without importing a router package before
 * the Storefront router is formally selected.
 */
export function createPvcSignRouteDefinition(
  props: PvcSignRouteProps,
): { path: typeof PVC_SIGN_ROUTE_PATH; element: ReactElement } {
  return {
    path: PVC_SIGN_ROUTE_PATH,
    element: <PvcSignRoute {...props} />,
  };
}
